<script lang="ts">
  // A vertical line between two panes. Position is in percent of the
  // positioned parent's width, measured from its left edge.
  let {
    position,
    min,
    max,
    defaultPosition,
    label,
    onMove,
    onDragChange,
  }: {
    position: number;
    min: number;
    max: number;
    defaultPosition: number;
    label: string;
    onMove: (position: number) => void;
    onDragChange: (dragging: boolean) => void;
  } = $props();

  let dragging = $state(false);
  const setDragging = (value: boolean) => {
    dragging = value;
    onDragChange(value);
  };

  const move = (to: number) => onMove(Math.min(max, Math.max(min, to)));

  const onpointerdown = (e: PointerEvent & { currentTarget: HTMLElement }) => {
    if (e.button !== 0) return;
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    setDragging(true);
  };

  const onpointermove = (e: PointerEvent & { currentTarget: HTMLElement }) => {
    if (!dragging) return;
    const parent = e.currentTarget.offsetParent;
    if (parent == null) return;
    const rect = parent.getBoundingClientRect();
    move(((e.clientX - rect.left) / rect.width) * 100);
  };

  const onkeydown = (e: KeyboardEvent) => {
    const step = e.shiftKey ? 5 : 1;
    if (e.key === 'ArrowLeft') move(position - step);
    else if (e.key === 'ArrowRight') move(position + step);
    else return;
    e.preventDefault();
  };
</script>

<!-- A focusable separator with a value is a widget (WAI-ARIA window splitter) -->
<!-- svelte-ignore a11y_no_noninteractive_tabindex, a11y_no_noninteractive_element_interactions -->
<div
  class="pane-divider"
  class:dragging
  style:left="{position}%"
  role="separator"
  aria-orientation="vertical"
  aria-label={label}
  aria-valuenow={Math.round(position)}
  aria-valuemin={Math.round(min)}
  aria-valuemax={Math.round(max)}
  tabindex="0"
  title="Drag to resize, double-click to reset"
  {onpointerdown}
  {onpointermove}
  onlostpointercapture={() => setDragging(false)}
  ondblclick={() => move(defaultPosition)}
  {onkeydown}
></div>

<style>
  .pane-divider {
    position: absolute;
    top: 0;
    bottom: 0;
    width: 0.5rem;
    transform: translateX(-50%);
    z-index: 2;
    cursor: col-resize;
    touch-action: none;
  }
  .pane-divider::after {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    left: 50%;
    width: 2px;
    transform: translateX(-50%);
    transition: background 0.15s;
  }
  .pane-divider:hover::after,
  .pane-divider:focus-visible::after,
  .pane-divider.dragging::after {
    background: var(--color-sky-400);
  }
  .pane-divider:focus-visible {
    outline: none;
  }
</style>
